import axios, { type AxiosRequestConfig } from 'axios';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import { chunk } from 'lodash';
import { type QueryOptions, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { useContainer } from 'unstated-next';

import { getUrls } from '../../../utils/url';
import InviteStudentsContainer from '../InviteStudentsContainer';

enum Scopes {
    ReadTeam = 'Team.ReadBasic.All',
    ReadMembers = 'TeamMember.Read.All',
    ReadUser = 'User.ReadBasic.All'
}

interface MsGraphRequest extends AxiosRequestConfig {
    msToken: string;
    clientId?: string;
    scope: Scopes;
}

async function msgraph<T, IsBatch = false>(request: MsGraphRequest) {
    const { api, web, webGlobal } = getUrls();

    request.baseURL = `${api}/v1/msgraph`;
    request.headers = { Authorization: `Bearer ${request.msToken}`, ...request.headers };
    request.params = { scope: request.scope, ...request.params };

    try {
        const response = await axios.request<
            IsBatch extends true
                ? {
                      responses: Array<{
                          id: string;
                          status: number;
                          headers: any;
                          body: T;
                      }>;
                  }
                : { value: T }
        >(request);

        return response.data;
    } catch (error) {
        if (!axios.isAxiosError(error) || error.response?.status !== 401 || !request.clientId) {
            throw error;
        }

        const params = new URLSearchParams({
            url: `${web}/teams`,
            client_id: request.clientId,
            scopes: Object.values(Scopes).join(' ')
        });
        const destination = `${webGlobal}/teams/index.html?${params}`;
        console.info('Switching parent to', destination);
        window.parent.postMessage(
            {
                func: 'page_redirection',
                data: { destination }
            },
            '*'
        );
        // Wait here until we vanish due to the above redirect
        await new Promise((resolved) => setTimeout(resolved, 5000));
    }
}

export const useMyTeams = (
    props: QueryOptions & { msToken: string | undefined }
): UseQueryResult<MicrosoftGraph.Team[]> => {
    const { msToken, ...rest } = props;

    const {
        settings: { azure_client_id: clientId }
    } = useContainer(InviteStudentsContainer);

    return useQuery({
        queryKey: ['msgraph/me/joinedTeams'],
        queryFn: async () => {
            if (!msToken) {
                return undefined;
            }

            const response = await msgraph<MicrosoftGraph.Team[]>({
                url: 'me/joinedTeams',
                msToken,
                clientId,
                scope: Scopes.ReadTeam,
                params: {
                    $select: 'id,displayName'
                }
            });

            return response?.value;
        },
        enabled: !!msToken,
        refetchOnWindowFocus: false,
        ...rest
    });
};

export const useTeamMembers = (
    props: QueryOptions & {
        teamId: MicrosoftGraph.Team['id'];
        open: boolean;
        msToken: string | undefined;
    }
): UseQueryResult<MicrosoftGraph.User[]> => {
    const { teamId, open, msToken, ...rest } = props;

    const {
        settings: { azure_client_id: clientId }
    } = useContainer(InviteStudentsContainer);

    return useQuery({
        queryKey: ['msgraph/teams/{teamId}/members', teamId],
        queryFn: async () => {
            if (!msToken || !teamId) {
                return undefined;
            }

            const members = await msgraph<MicrosoftGraph.AadUserConversationMember[]>({
                url: `teams/${teamId}/members`,
                msToken,
                clientId,
                scope: Scopes.ReadMembers
            });
            // Graph API only allows 20 requests per batch
            const membersChunks = chunk(members?.value, 20);

            const users: MicrosoftGraph.User[] = [];

            await Promise.all(
                membersChunks.map(async (membersChunk) => {
                    const membersJson = {
                        requests: membersChunk.map((member) => ({
                            id: member.userId,
                            method: 'GET',
                            url: `/users/${member.userId}?$select=displayName,givenName,surname,userPrincipalName`
                        }))
                    };

                    const responses = await msgraph<MicrosoftGraph.AadUserConversationMember, true>({
                        method: 'POST',
                        url: '$batch',
                        msToken,
                        scope: Scopes.ReadUser,
                        data: membersJson
                    });

                    responses?.responses.forEach((response) => {
                        users.push(response.body);
                    });
                })
            );

            return users.sort((a, b) => a.displayName!.localeCompare(b.displayName!));
        },
        enabled: !!msToken && !!teamId && open,
        refetchOnWindowFocus: false,
        ...rest
    });
};
